import React, { KeyboardEvent, useEffect, useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { StyleSettings } from "../../styles/StyleSettings";
import "dayjs/locale/ko";

interface SnDateBoxProps {
  defaultValue?: string;
  onValueChanged?: (value: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

const SnDateBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnDateBoxProps) => {
    const [value, setValue] = useState<Dayjs | null>(
      defaultValue ? dayjs(defaultValue) : null
    );
    const [error, setError] = useState<boolean>(false);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    const fetchDefaultValue = async () => {
      try {
        if (defaultValue) {
          setValue(dayjs(defaultValue));
        } else {
          setValue(null);
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    const handleChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) setError(false);

      setValue(newValue);

      if (onValueChanged) {
        if (newValue) {
          const date = newValue.format("YYYYMMDD");
          if (date !== "Invalid Date") {
            onValueChanged(date);
          } else {
            onValueChanged("");
          }
        } else {
          onValueChanged("");
        }
      }
    };

    const handleError = (reason: any) => {
      // 일부만 입력된 경우를 에러로 판단
      if (value && reason === "invalidDate") {
        setError(true);
      } else {
        setError(false);
      }
    };

    const handleAccept = () => {
      setTimeout(() => {
        onFocus && onFocus("current");
      }, 10);
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <DatePicker
          inputRef={inputRef}
          format="YYYY/MM/DD"
          value={value ? dayjs(value) : null}
          onChange={handleChange}
          onAccept={handleAccept}
          onError={handleError}
          disabled={isReadOnly}
          slotProps={{
            textField: {
              error: error,
              placeholder: "",
              // helperText: error ? "년/월/일을 모두 입력해주세요." : "",
              FormHelperTextProps: {
                sx: {
                  backgroundColor: "rgb(243 244 246)",
                  margin: 0,
                  padding: 1,
                },
              },
              onKeyDown: handleKeyDown,
            },
            popper: {
              sx: {
                "& .MuiDateCalendar-root": {
                  height: "auto",
                },
                "& .MuiPickersSlideTransition-root": {
                  minHeight: "205px",
                },
              },
            },
            openPickerButton: {
              tabIndex: -1, // Tab 키로 포커스 안 가게 설정
            },
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": {
              height: StyleSettings.control_height,
              fontSize: StyleSettings.font,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
            "& .MuiSvgIcon-root": {
              fontSize: StyleSettings.font,
            },
            backgroundColor: backgroundColor,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
          }}
        />
      </LocalizationProvider>
    );
  }
);

export default SnDateBox;
